import { useMapUiStore } from '@/stores/v2/map-ui-store.ts';

const GEOLOCATION_REJECTED_KEY = 'geolocation-rejected';

export interface LocationCoordinates {
   latitude: string;
   longitude: string;
}

/**
 * Composable for requesting and managing user's geolocation for chat metadata.
 *
 * Decision matrix when getting location for chat:
 * 1. If location is known → return it
 * 2. If location is unknown AND not previously rejected → prompt for permission
 * 3. If location is unknown AND previously/just rejected → return null
 */
export function useUserGeolocation() {
   const mapUiStore = useMapUiStore();

   /**
    * Checks if the user has previously rejected geolocation permission for chat.
    */
   function wasRejectedForChat(): boolean {
      return localStorage.getItem(GEOLOCATION_REJECTED_KEY) === 'true';
   }

   /**
    * Marks geolocation as rejected for chat in localStorage.
    */
   function markAsRejectedForChat(): void {
      localStorage.setItem(GEOLOCATION_REJECTED_KEY, 'true');
   }

   /**
    * Requests the user's current geolocation and stores it in mapUiStore.
    * Does NOT track rejection - use getLocationForChat() for chat with rejection tracking.
    * @returns Promise<boolean> - true if location was obtained, false otherwise
    */
   async function requestUserLocation(): Promise<boolean> {
      // Skip if we already have the user's location
      if ((mapUiStore.userLocation.latitude || mapUiStore.userLocation.latitude === 0) && (mapUiStore.userLocation.longitude || mapUiStore.userLocation.longitude === 0)) {
         return true;
      }

      // Check if geolocation is supported
      if (!navigator.geolocation) {
         return false;
      }

      return new Promise((resolve) => {
         navigator.geolocation.getCurrentPosition(
            (position) => {
               mapUiStore.userLocation.latitude = position.coords.latitude;
               mapUiStore.userLocation.longitude = position.coords.longitude;
               resolve(true);
            },
            () => {
               // Don't mark as rejected here - let the caller decide
               resolve(false);
            },
         );
      });
   }

   /**
    * Gets location for chat metadata using decision matrix:
    * 1. If location is known → return it
    * 2. If location is unknown AND not previously rejected → prompt, then return result or null
    * 3. If location is unknown AND previously rejected → return null
    *
    * This function DOES track rejection in localStorage.
    *
    * @returns Location coordinates formatted for chat metadata, or null
    */
   async function getLocationForChat(): Promise<LocationCoordinates | null> {
      // Case 1: Location already known
      if ((mapUiStore.userLocation.latitude || mapUiStore.userLocation.latitude === 0) && (mapUiStore.userLocation.longitude || mapUiStore.userLocation.longitude === 0)) {
         return {
            latitude: mapUiStore.userLocation.latitude.toFixed(6),
            longitude: mapUiStore.userLocation.longitude.toFixed(6),
         };
      }

      // Case 3: Previously rejected - don't prompt again
      if (wasRejectedForChat()) {
         return null;
      }

      // Case 2: Unknown and not rejected - prompt for permission
      const granted = await requestUserLocation();

      if (granted) {
         return {
            latitude: mapUiStore.userLocation.latitude.toFixed(6),
            longitude: mapUiStore.userLocation.longitude.toFixed(6),
         };
      }

      // User rejected - mark it so we don't prompt again
      markAsRejectedForChat();
      return null;
   }

   return {
      requestUserLocation,
      getLocationForChat,
   };
}
